//- ANGULAR
import { Component } from '@angular/core';
import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
//- BACKEND
import {
  IPaypalReceiptDto,
  IPaypalPostReceiptDto,
  PaypalPostReceiptDto,
  PaypalReceiptClient,
  PaypalReceiptDto,
  GetPaypalReceiptsOfTodayQuery,
  FileParameter,
} from '@tech/api-clients/receipts';

//- OWN
import { PermanentTableComponent } from '@features/receipts/components/permanent-table/permanent-table.component';
import {
  IFilters,
  PermanentTablePaginatedComponent,
} from '@features/receipts/components/permanent-table-paginated/permanent-table-paginated.component';
import { PrettyConsole } from '@app/shared/utils';
import { FileProcessorService } from '@app/shared/services/wasm/file-processor/file-processor.service';
import { NotificationService } from '@app/shared/services/notification/notification.service';
import { NotificationhubService } from '@app/shared/services/notificationhub/notificationhub.service';
//~ BEGIN
//
import { ReceiptComponent } from '@features/receipts/components/receipt/receipt.component';
import { TranslateModule } from '@ngx-translate/core';

interface ReceiptDisplay extends IPaypalPostReceiptDto {
  file: string;
  ok: boolean;
  [key: string]: string | number | boolean | Date | undefined;
}

@Component({
  selector: 'app-receipt-paypal',
  standalone: true,
  imports: [ReceiptComponent, TranslateModule],
  templateUrl: './receipt-paypal.component.html',
  styleUrl: './receipt-paypal.component.scss',
})
export class ReceiptPaypalComponent extends ReceiptComponent {
  //displaying
  stagedReceipts: ReceiptDisplay[] = [];
  available_f: IFilters[] = [];
  displayedColumns: string[] = [
    'file',
    'err_line',
    ...Object.keys(PaypalPostReceiptDto.prototype.toJSON()),
  ];

  override sse_name: string = 'PaypalReceipt';

  validation_s = {
    fecha: {
      type: 'regex',
      pattern: '\\d{2}/\\d{2}/\\d{4}|\\d{4}/\\d{2}/\\d{2}',
    },
    hora: {
      type: 'regex',
      pattern: '^([01]\\d|2[0-3]):[0-5]\\d(?<secs>:[0-5]\\d)?$',
    }
  };

  _wasm_split_pattern: string = '","|,"';

  client_proc = (files: FileParameter[]) => {
    return this.child_client.uploadPaypalFiles(files);
  };

  constructor(
    dialog: MatDialog,
    fileProcessor: FileProcessorService,
    notify: NotificationService,
    hub: NotificationhubService,
    private child_client: PaypalReceiptClient
  ) {
    super(dialog, fileProcessor, notify, hub);
    this.sse_name = 'PaypalReceipt';
    this.upload_handler = this.client_proc.bind(this);
    this.callme = this.lookup_all.bind(this);
    this.get_paginated_data = this.get_paginated_data.bind(this);
    this.available_f = [];
    this.child_client.getFilterStructure(undefined).subscribe({
      next: (x) => {
        var parsed = JSON.parse(x);
        this.available_f = parsed;
      },
      error: (e) => {
        PrettyConsole.ERROR(
          `Something failed while getting filter structure ${e}`
        );
      },
    });
  }

  lookup_today = (_: Event) => {
    const loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    const ds = new PermanentPaypalReceipts();

    this.child_client
      .getPaypalReceiptsOfToday(GetPaypalReceiptsOfTodayQuery.fromJS({}))
      .subscribe({
        next: (receipts) => {
          ds.setData(receipts);
          loading$.next(false);
        },
        error: () => loading$.next(false),
      });

    this.dialog.open(PermanentTableComponent<PermanentPaypalReceipts>, {
      width: '85vw',
      height: '80vh',
      data: {
        receipts: ds,
        display: Object.keys(PaypalReceiptDto.prototype.toJSON()),
        loading$,
        callback: this.callme,
      },
    });
  };

  lookup_all = () => {
    this.dialog.closeAll();
    const data_source = new PermanentPaypalReceipts();
    this.dialog.open(PermanentTablePaginatedComponent<IPaypalReceiptDto>, {
      width: '100vw',
      maxWidth: '98vw',
      height: '95vh',
      data: {
        receipts: data_source,
        display: Object.keys(PaypalReceiptDto.prototype.toJSON()).filter(
          (d) => d !== 'id'
        ),
        callback: this.get_paginated_data,
        filters: this.available_f,
      },
    });
  };

  override get_paginated_data = (
    page_index: number,
    page_size: number,
    filters: IFilters[]
  ) => {
    if (this.available_f.length === 0) {
      this.child_client.getFilterStructure(undefined).subscribe({
        next: (x) => {
          var parsed = JSON.parse(x);
          this.available_f = parsed;
        },
        error: (e) => {
          PrettyConsole.ERROR(
            `Something failed while getting filter structure ${e}`
          );
        },
      });
    }

    let data = new BehaviorSubject<IPaypalReceiptDto[]>([]);
    let count = new BehaviorSubject<number>(0);
    let filter_values: any = {};
    filters.forEach((f) => {
      filter_values[f.id] = f.value === null ? undefined : f.value;
    });

    this.child_client
      .getPaypalReceiptsOfAllTime(
        page_index,
        page_size,
        JSON.stringify(filter_values)
      )
      .subscribe({
        error: (e) => {
          PrettyConsole.ERROR(
            `Error while getting the paypal receipts of all time, page: ${page_index}, size: ${page_size}: ${e}`
          );
        },
        next: (d) => {
          if (d.count) count.next(d.count);
          if (d.receipts) data.next(d.receipts);
        },
      });
    return { count: count.asObservable(), data: data.asObservable() };
  };
}

export class PermanentPaypalReceipts extends DataSource<IPaypalReceiptDto> {
  private data = new BehaviorSubject<IPaypalReceiptDto[]>([]);

  connect(): Observable<IPaypalReceiptDto[]> {
    return this.data.asObservable();
  }

  addRow(row: IPaypalReceiptDto) {
    const current = this.data.getValue();
    this.data.next([...current, row]);
  }

  setData(rows: PaypalReceiptDto[]) {
    const data = rows.map((r) => r.toJSON());
    this.data.next(data);
  }

  disconnect(): void {}
}
